import { gql } from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';
import DataGrid, {
  Column,
  Scrolling,
  SearchPanel,
} from 'devextreme-react/data-grid';
import NumberBox from 'devextreme-react/number-box';
import Popup from 'devextreme-react/popup';
import TabPanel, { Item as TabItem } from 'devextreme-react/tab-panel';
import TextArea from 'devextreme-react/text-area';
import TextBox from 'devextreme-react/text-box';
import Toolbar, { Item as ToolbarItem } from 'devextreme-react/toolbar';
import notify from 'devextreme/ui/notify';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { logout, useUsername } from '@/core/auth/auth';

const TRIGGER_UNIQUE_BUILD = gql`
  mutation TriggerUniqueBuild($sha: String, $ref: String) {
    triggerUniqueBuild(sha: $sha, ref: $ref) {
      id
      url
      status
      conclusion
      action
      stackId
    }
  }
`;

const TRIGGER_UNIQUE_PUBLISH = gql`
  mutation TriggerUniquePublish($sha: String, $dryRun: Boolean) {
    triggerUniquePublish(sha: $sha, dryRun: $dryRun) {
      id
      url
      status
      conclusion
      action
      stackId
    }
  }
`;

const MIGRATE_UNIQUE_DB_API = gql`
  mutation MigrateUniqueDbApi($action: String!, $confirm: String) {
    migrateUniqueDbApi(action: $action, confirm: $confirm) {
      id
      url
      status
      conclusion
      action
      stackId
    }
  }
`;

const YAZILIM_TALEP_LANE_STATUS = gql`
  query YazilimTalepLaneStatus($talepId: Int!) {
    yazilimTalepLaneStatus(talepId: $talepId) {
      talepId
      tipSetHash
      kontrolVerdict
      kontrolFresh
      testReadyForUniqueSha
      statusPhase
      statusInFlight
      statusLabelTr
      tipSet {
        unique
        react
        rest
      }
      surface {
        touchesUnique
        hasDbScripts
        delphiOnly
        hasLiveSql
        portalOnly
        testApplicable
        geriAlAvailable
      }
      planla {
        visible
        enabled
        hardBlockers
        warnings
      }
      uygula {
        visible
        enabled
        hardBlockers
        warnings
      }
      kontrol {
        visible
        enabled
        hardBlockers
        warnings
      }
      test {
        visible
        enabled
        hardBlockers
        warnings
      }
      kabul {
        visible
        enabled
        hardBlockers
        warnings
      }
      guncelle {
        visible
        enabled
        hardBlockers
        warnings
        requiresKontrolAtla
        requiresCanliConfirm
        deployCompatibility
        mergePhase
        mergeState
      }
      geriAl {
        visible
        enabled
        hardBlockers
        warnings
      }
    }
  }
`;

const YAZILIM_TALEP_LANE_SUMMARIES = gql`
  query YazilimTalepLaneSummaries($limit: Int) {
    yazilimTalepLaneSummaries(limit: $limit) {
      talepId
      updatedAt
      planla
      uygula
      kontrol
      kontrolVerdict
      test
      kabul
      guncelle
      geriAl
      lastLane
    }
  }
`;

const START_YAZILIM_LANE = gql`
  mutation StartYazilimLane(
    $talepId: Int!
    $lane: String!
    $kontrolAtla: String
    $kontrolAtlaReason: String
    $canliConfirm: String
    $geriAlConfirm: String
  ) {
    startYazilimLane(
      talepId: $talepId
      lane: $lane
      kontrolAtla: $kontrolAtla
      kontrolAtlaReason: $kontrolAtlaReason
      canliConfirm: $canliConfirm
      geriAlConfirm: $geriAlConfirm
    ) {
      ok
      talepId
      lane
      runId
      fencingVersion
      message
      detail
      checklistVerdict
      agentId
      agentRunId
      url
    }
  }
`;

const YAZILIM_TALEP_SHIP_CONTEXT = gql`
  query YazilimTalepShipContext($talepId: Int!) {
    yazilimTalepShipContext(talepId: $talepId) {
      talepId
      tipSha
      branchName
      statusPhase
      statusLabelTr
      canAccept
      latestRunId
      latestRunStatus
      latestRunPreview
      prNumber
      prUrl
      prState
      dbScripts
      verdict
      visibility {
        delphiOnly
        hasDbScripts
        hasUniqueDbTargets
        hasLiveSql
        hasOpenPr
        hasBak
      }
      boards {
        name
        status
        lastExec
        lastDuration
        runCount
        minPeriod
        reportServer
        sqlTextHead
        found
        ageSeconds
        staleAfterSeconds
      }
      boardsError
      rawJson
    }
  }
`;

const YAZILIM_TALEP_AGENT_RUNS = gql`
  query YazilimTalepAgentRuns($talepId: Int!) {
    yazilimTalepAgentRuns(talepId: $talepId) {
      talepId
      runId
      cursorAgentId
      cursorRunId
      status
      outcomeKind
      startedAt
      finishedAt
      branchName
      summaryForUser
      preview
      error
    }
  }
`;

const YAZILIM_TALEP_AGENT_RUN = gql`
  query YazilimTalepAgentRun($talepId: Int!, $runId: ID!) {
    yazilimTalepAgentRun(talepId: $talepId, runId: $runId) {
      talepId
      runId
      status
      outcomeKind
      startedAt
      finishedAt
      summaryForUser
      preview
      error
      prompt
      agentResult
      cursorAgentId
    }
  }
`;

const YAZILIM_TALEP_BOARDS_HEALTH = gql`
  query YazilimTalepBoardsHealth($talepId: Int!, $channels: [String!]) {
    yazilimTalepBoardsHealth(talepId: $talepId, channels: $channels) {
      channels
      serverNow
      error
      rows {
        name
        status
        lastExec
        lastDuration
        runCount
        minPeriod
        reportServer
        sqlTextHead
        found
        ageSeconds
        staleAfterSeconds
      }
    }
  }
`;

const WORKFLOW_RUN = gql`
  query WorkflowRun($id: ID!) {
    workflowRun(id: $id) {
      id
      url
      status
      conclusion
      action
      stackId
      logs
      jobs {
        id
        name
        status
        conclusion
        steps {
          name
          number
          status
          conclusion
        }
      }
    }
  }
`;

type WorkflowStep = {
  name: string;
  number: number;
  status: string;
  conclusion?: string | null;
};

type WorkflowJob = {
  id: string;
  name: string;
  status: string;
  conclusion?: string | null;
  steps: WorkflowStep[];
};

type WorkflowRun = {
  id: string;
  url?: string | null;
  status: string;
  conclusion?: string | null;
  action: string;
  stackId: string;
  logs?: string | null;
  jobs?: WorkflowJob[] | null;
};

type LaneButtonStatus = {
  visible: boolean;
  enabled: boolean;
  hardBlockers: string[];
  warnings: string[];
};

type GuncelleLaneStatus = LaneButtonStatus & {
  requiresKontrolAtla: boolean;
  requiresCanliConfirm: boolean;
  deployCompatibility?: string | null;
  mergePhase?: string | null;
  mergeState?: string | null;
};

type YazilimTalepLaneStatus = {
  talepId: number;
  tipSetHash?: string | null;
  kontrolVerdict?: string | null;
  kontrolFresh: boolean;
  testReadyForUniqueSha?: string | null;
  statusPhase: string;
  statusInFlight: boolean;
  statusLabelTr: string | null;
  tipSet: {
    unique?: string | null;
    react?: string | null;
    rest?: string | null;
  };
  surface: {
    touchesUnique: boolean;
    hasDbScripts: boolean;
    delphiOnly: boolean;
    hasLiveSql: boolean;
    portalOnly: boolean;
    testApplicable: boolean;
    geriAlAvailable: boolean;
  };
  planla: LaneButtonStatus;
  uygula: LaneButtonStatus;
  kontrol: LaneButtonStatus;
  test: LaneButtonStatus;
  kabul: LaneButtonStatus;
  guncelle: GuncelleLaneStatus;
  geriAl: LaneButtonStatus;
};

type StartYazilimLaneResult = {
  ok: boolean;
  talepId: number;
  lane: string;
  runId?: string | null;
  fencingVersion?: number | null;
  message: string;
  detail?: string | null;
  checklistVerdict?: string | null;
  agentId?: string | null;
  agentRunId?: string | null;
  url?: string | null;
};

type YazilimTalepLaneSummary = {
  talepId: number;
  updatedAt: string;
  planla: boolean;
  uygula: boolean;
  kontrol: boolean;
  kontrolVerdict?: string | null;
  test: boolean;
  kabul: boolean;
  guncelle: boolean;
  geriAl: boolean;
  lastLane?: string | null;
};

type ShipVisibility = {
  delphiOnly: boolean;
  hasDbScripts: boolean;
  hasUniqueDbTargets: boolean;
  hasLiveSql: boolean;
  hasOpenPr: boolean;
  hasBak: boolean;
};

type BoardsHealthRow = {
  name: string;
  status: string;
  lastExec?: string | null;
  lastDuration?: number | null;
  runCount?: number | null;
  minPeriod: number;
  reportServer: boolean;
  sqlTextHead: string;
  found: boolean;
  ageSeconds?: number | null;
  staleAfterSeconds: number;
};

type YazilimTalepShipContext = {
  talepId: number;
  tipSha?: string | null;
  branchName: string;
  statusPhase: string;
  statusLabelTr: string;
  canAccept: boolean;
  latestRunId?: string | null;
  latestRunStatus?: string | null;
  latestRunPreview?: string | null;
  prNumber?: number | null;
  prUrl?: string | null;
  prState?: string | null;
  dbScripts: string[];
  verdict: string;
  visibility: ShipVisibility;
  boards: BoardsHealthRow[];
  boardsError?: string | null;
  rawJson: string;
};

type YazilimTalepAgentRunSummary = {
  talepId: number;
  runId: string;
  cursorAgentId?: string | null;
  cursorRunId?: string | null;
  status: string;
  outcomeKind?: string | null;
  startedAt: string;
  finishedAt?: string | null;
  branchName?: string | null;
  summaryForUser?: string | null;
  preview?: string | null;
  error?: string | null;
};

type YazilimTalepAgentRunDetail = YazilimTalepAgentRunSummary & {
  prompt: string;
  agentResult: string;
};

const GRID_COMMON = {
  showBorders: true,
  showRowLines: true,
  showColumnLines: false,
  rowAlternationEnabled: true,
  hoverStateEnabled: true,
  columnAutoWidth: true,
  wordWrapEnabled: true,
  allowColumnReordering: true,
} as const;

const statusClass = (status: string) => {
  const s = status.toUpperCase();
  if (s === 'HEALTHY' || s === 'AVAILABLE') return 'status-healthy';
  if (s === 'DEGRADED') return 'status-degraded';
  if (s === 'DOWN' || s === 'MISSING') return 'status-down';
  return 'status-unknown';
};

const statusLabel = (status: string, conclusion?: string | null) => {
  if (status === 'completed') return conclusion ?? 'completed';
  return status;
};

const laneBoolMark = (value: boolean) => (value ? '✓' : '—');

const workflowTitle = (action: string, name: string) => {
  switch (action) {
    case 'apply':
      return `Deploy · ${name}`;
    case 'plan':
      return `Plan · ${name}`;
    case 'build':
      return `Build Unique · ${name || 'Delphi'}`;
    case 'publish':
      return `Publish Unique · ${name || 'Delphi'}`;
    case 'deploy-image':
      return `Deploy image · ${name}`;
    default:
      return `${action} · ${name}`;
  }
};

const showNotify = (
  message: string,
  type: 'success' | 'error' | 'info' | 'warning' = 'info',
) => {
  try {
    notify({
      message,
      type,
      displayTime: 4500,
      position: { at: 'top right', my: 'top right', offset: '0 12' },
      width: 360,
    });
  } catch {
    // never let toast failures take down the page
  }
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`status-pill ${statusClass(status)}`}>{status}</span>
);

const LogViewer: React.FC<{ text: string; loading?: boolean }> = ({
  text,
  loading,
}) => {
  const logRef = useRef<HTMLPreElement>(null);
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [text]);

  return (
    <pre ref={logRef} className="log-viewer">
      {loading ? 'Loading…' : text || 'No log output.'}
    </pre>
  );
};

export const DashboardPage: React.FC = () => {
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [activeStackName, setActiveStackName] = useState('');
  const [liveRun, setLiveRun] = useState<WorkflowRun | null>(null);
  const [uniqueSha, setUniqueSha] = useState('');
  const [uniqueRef, setUniqueRef] = useState('');
  const [uniqueDryRun, setUniqueDryRun] = useState(false);
  const [uniqueBusy, setUniqueBusy] = useState<'build' | 'publish' | null>(
    null,
  );
  const [talepId, setTalepId] = useState<number | null>(null);
  const [shipContext, setShipContext] =
    useState<YazilimTalepShipContext | null>(null);
  const [shipBusy, setShipBusy] = useState(false);
  const [agentRuns, setAgentRuns] = useState<YazilimTalepAgentRunSummary[]>(
    [],
  );
  const [selectedAgentRun, setSelectedAgentRun] =
    useState<YazilimTalepAgentRunDetail | null>(null);
  const [boardsRows, setBoardsRows] = useState<BoardsHealthRow[]>([]);
  const [boardsError, setBoardsError] = useState<string | null>(null);
  const [canliConfirm, setCanliConfirm] = useState('');
  const [kontrolAtla, setKontrolAtla] = useState('');
  const [kontrolAtlaReason, setKontrolAtlaReason] = useState('');
  const [geriAlConfirm, setGeriAlConfirm] = useState('');
  const [laneStatus, setLaneStatus] = useState<YazilimTalepLaneStatus | null>(
    null,
  );
  const [laneResult, setLaneResult] = useState<StartYazilimLaneResult | null>(
    null,
  );
  const [uniqueDbApiBusy, setUniqueDbApiBusy] = useState<
    'validate' | 'migrate' | null
  >(null);
  const username = useUsername();

  const [triggerUniqueBuild] = useMutation<
    { triggerUniqueBuild: WorkflowRun },
    { sha?: string | null; ref?: string | null }
  >(TRIGGER_UNIQUE_BUILD);
  const [triggerUniquePublish] = useMutation<
    { triggerUniquePublish: WorkflowRun },
    { sha?: string | null; dryRun?: boolean | null }
  >(TRIGGER_UNIQUE_PUBLISH);
  const [migrateUniqueDbApi] = useMutation<
    { migrateUniqueDbApi: WorkflowRun },
    { action: string; confirm?: string | null }
  >(MIGRATE_UNIQUE_DB_API);
  const [loadLaneStatus] = useLazyQuery<
    { yazilimTalepLaneStatus: YazilimTalepLaneStatus },
    { talepId: number }
  >(YAZILIM_TALEP_LANE_STATUS, { fetchPolicy: 'network-only' });
  const { data: laneSummariesData, refetch: refetchLaneSummaries } = useQuery<
    { yazilimTalepLaneSummaries: YazilimTalepLaneSummary[] },
    { limit?: number | null }
  >(YAZILIM_TALEP_LANE_SUMMARIES, {
    variables: { limit: 50 },
    fetchPolicy: 'network-only',
  });
  const [startYazilimLane] = useMutation<
    { startYazilimLane: StartYazilimLaneResult },
    {
      talepId: number;
      lane: string;
      kontrolAtla?: string | null;
      kontrolAtlaReason?: string | null;
      canliConfirm?: string | null;
      geriAlConfirm?: string | null;
    }
  >(START_YAZILIM_LANE);
  const [loadShipContext] = useLazyQuery<
    { yazilimTalepShipContext: YazilimTalepShipContext },
    { talepId: number }
  >(YAZILIM_TALEP_SHIP_CONTEXT, { fetchPolicy: 'network-only' });
  const [loadAgentRuns] = useLazyQuery<
    { yazilimTalepAgentRuns: YazilimTalepAgentRunSummary[] },
    { talepId: number }
  >(YAZILIM_TALEP_AGENT_RUNS, { fetchPolicy: 'network-only' });
  const [loadAgentRun] = useLazyQuery<
    { yazilimTalepAgentRun: YazilimTalepAgentRunDetail | null },
    { talepId: number; runId: string }
  >(YAZILIM_TALEP_AGENT_RUN, { fetchPolicy: 'network-only' });
  const [loadBoardsHealth] = useLazyQuery<
    {
      yazilimTalepBoardsHealth: {
        channels: string[];
        serverNow?: string | null;
        error?: string | null;
        rows: BoardsHealthRow[];
      };
    },
    { talepId: number; channels?: string[] | null }
  >(YAZILIM_TALEP_BOARDS_HEALTH, { fetchPolicy: 'network-only' });
  const [loadRun] = useLazyQuery<
    { workflowRun: WorkflowRun },
    { id: string }
  >(WORKFLOW_RUN, { fetchPolicy: 'network-only' });

  const workflowSteps = useMemo(() => {
    const jobs = liveRun?.jobs ?? [];
    return jobs.flatMap((j) =>
      j.steps.map((s) => ({
        ...s,
        jobName: j.name,
        key: `${j.id}-${s.number}`,
      })),
    );
  }, [liveRun]);

  useEffect(() => {
    if (!activeRunId || activeRunId === 'unknown') return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      try {
        const res = await loadRun({ variables: { id: activeRunId } });
        if (cancelled) return;
        const next = res.data?.workflowRun;
        if (!next) return;
        setLiveRun(next);
        if (next.status === 'completed') return;
        timer = setTimeout(() => void tick(), 3000);
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        showNotify(`Failed to refresh run: ${message}`, 'error');
        timer = setTimeout(() => void tick(), 5000);
      }
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activeRunId, loadRun]);

  const handleUniqueDbApi = (action: 'validate' | 'migrate') => {
    if (action === 'migrate' && canliConfirm.trim() !== 'CANLI UYGULA') {
      showNotify('Type CANLI UYGULA to migrate UNIQUE_DB.api on canlı.', 'error');
      return;
    }
    setUniqueDbApiBusy(action);
    void migrateUniqueDbApi({
      variables: {
        action,
        confirm: action === 'migrate' ? canliConfirm.trim() : null,
      },
    })
      .then((res) => {
        const workflow = res.data?.migrateUniqueDbApi;
        if (!workflow) {
          showNotify('No workflow run returned for UNIQUE_DB.api.', 'error');
          return;
        }
        setLiveRun(workflow);
        setActiveRunId(workflow.id);
        setActiveStackName('UNIQUE_DB.api');
        if (action === 'migrate') setCanliConfirm('');
        showNotify(
          `UNIQUE_DB.api ${action} started`,
          'success',
        );
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        showNotify(`UNIQUE_DB.api ${action} failed: ${message}`, 'error');
      })
      .finally(() => setUniqueDbApiBusy(null));
  };

  const handleUniqueAction = (kind: 'build' | 'publish') => {
    setUniqueBusy(kind);
    setLiveRun(null);
    setActiveRunId(null);
    setActiveStackName(kind === 'build' ? 'build-delphi' : 'publish-delphi');

    const sha = uniqueSha.trim() || null;
    const run =
      kind === 'build'
        ? triggerUniqueBuild({
            variables: {
              sha,
              ref: uniqueRef.trim() || null,
            },
          }).then((res) => res.data?.triggerUniqueBuild)
        : triggerUniquePublish({
            variables: {
              sha,
              dryRun: uniqueDryRun,
            },
          }).then((res) => res.data?.triggerUniquePublish);

    void run
      .then((workflow) => {
        if (!workflow) {
          showNotify(`No workflow run returned for Unique ${kind}.`, 'error');
          return;
        }
        setLiveRun(workflow);
        setActiveRunId(workflow.id);
        showNotify(
          `Unique ${kind}${uniqueDryRun && kind === 'publish' ? ' (dry run)' : ''} started`,
          'info',
        );
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        showNotify(`Failed to start Unique ${kind}: ${message}`, 'error');
      })
      .finally(() => setUniqueBusy(null));
  };

  const requireTalepId = (): number | null => {
    if (talepId == null || !Number.isInteger(talepId) || talepId <= 0) {
      showNotify('Enter a valid TalepId.', 'error');
      return null;
    }
    return talepId;
  };

  const refreshLaneStatus = async (id: number): Promise<void> => {
    const res = await loadLaneStatus({ variables: { talepId: id } });
    const st = res.data?.yazilimTalepLaneStatus;
    if (st) setLaneStatus(st);
  };

  const handleLoadLaneStatus = (overrideId?: number) => {
    const id =
      overrideId != null && Number.isInteger(overrideId) && overrideId > 0
        ? overrideId
        : requireTalepId();
    if (id == null) return;
    if (overrideId != null) setTalepId(id);
    setShipBusy(true);
    void refreshLaneStatus(id)
      .then(async () => {
        showNotify(`Lane status loaded for ${id}`, 'success');
        await refetchLaneSummaries().catch(() => undefined);
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        showNotify(`Lane status failed: ${message}`, 'error');
      })
      .finally(() => setShipBusy(false));
  };

  const handleStartLane = (lane: string) => {
    const id = requireTalepId();
    if (id == null) return;
    const g = laneStatus?.guncelle;
    if (lane === 'guncelle' && g?.requiresKontrolAtla) {
      if (kontrolAtla.trim() !== 'KONTROL ATLA') {
        showNotify('Type KONTROL ATLA to override stale Kontrol.', 'error');
        return;
      }
      if (!kontrolAtlaReason.trim()) {
        showNotify('kontrolAtlaReason is required with KONTROL ATLA.', 'error');
        return;
      }
    }
    if (lane === 'guncelle' && g?.requiresCanliConfirm) {
      const phrase = canliConfirm.trim();
      if (phrase !== 'CANLI UYGULA') {
        showNotify('Type CANLI UYGULA for live SQL.', 'error');
        return;
      }
    }
    if (lane === 'geriAl' && geriAlConfirm.trim() !== 'CANLI GERI AL') {
      showNotify('Type CANLI GERI AL to confirm restore.', 'error');
      return;
    }
    setShipBusy(true);
    void startYazilimLane({
      variables: {
        talepId: id,
        lane,
        kontrolAtla:
          lane === 'guncelle' && g?.requiresKontrolAtla
            ? kontrolAtla.trim()
            : null,
        kontrolAtlaReason:
          lane === 'guncelle' && g?.requiresKontrolAtla
            ? kontrolAtlaReason.trim()
            : null,
        canliConfirm:
          lane === 'guncelle' && g?.requiresCanliConfirm
            ? canliConfirm.trim()
            : null,
        geriAlConfirm: lane === 'geriAl' ? geriAlConfirm.trim() : null,
      },
    })
      .then(async (res) => {
        const body = res.data?.startYazilimLane;
        if (!body) {
          showNotify(`No ${lane} result.`, 'error');
          return;
        }
        setLaneResult(body);
        showNotify(body.message, body.ok ? 'success' : 'warning');
        await refreshLaneStatus(id).catch(() => undefined);
        await refetchLaneSummaries().catch(() => undefined);
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        showNotify(`${lane} failed: ${message}`, 'error');
      })
      .finally(() => setShipBusy(false));
  };

  const handleLoadShipContext = () => {
    const id = requireTalepId();
    if (id == null) return;
    setShipBusy(true);
    void Promise.all([
      loadShipContext({ variables: { talepId: id } }),
      loadAgentRuns({ variables: { talepId: id } }),
      loadLaneStatus({ variables: { talepId: id } }),
    ])
      .then(([ctxRes, runsRes, laneRes]) => {
        const ctx = ctxRes.data?.yazilimTalepShipContext;
        if (!ctx) {
          showNotify('No ship context returned.', 'error');
          return;
        }
        setShipContext(ctx);
        setBoardsRows(ctx.boards);
        setBoardsError(ctx.boardsError ?? null);
        setAgentRuns(runsRes.data?.yazilimTalepAgentRuns ?? []);
        if (laneRes.data?.yazilimTalepLaneStatus) {
          setLaneStatus(laneRes.data.yazilimTalepLaneStatus);
        }
        showNotify(
          `Ship context loaded · verdict ${ctx.verdict}${ctx.tipSha ? ` · tip ${ctx.tipSha.slice(0, 8)}` : ''}`,
          'success',
        );
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        showNotify(`Failed to load ship context: ${message}`, 'error');
      })
      .finally(() => setShipBusy(false));
  };

  const handleProbeBoards = () => {
    const id = requireTalepId();
    if (id == null) return;
    setShipBusy(true);
    void loadBoardsHealth({ variables: { talepId: id, channels: null } })
      .then((res) => {
        const body = res.data?.yazilimTalepBoardsHealth;
        if (!body) {
          showNotify('No boards health returned.', 'error');
          return;
        }
        setBoardsRows(body.rows);
        setBoardsError(body.error ?? null);
        showNotify(
          body.error
            ? `Boards probe error: ${body.error}`
            : `Boards probe: ${body.rows.length} channel(s)`,
          body.error ? 'warning' : 'success',
        );
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        showNotify(`Boards probe failed: ${message}`, 'error');
      })
      .finally(() => setShipBusy(false));
  };

  const handleOpenAgentRun = (runId: string) => {
    const id = requireTalepId();
    if (id == null) return;
    void loadAgentRun({ variables: { talepId: id, runId } })
      .then((res) => {
        const detail = res.data?.yazilimTalepAgentRun;
        if (!detail) {
          showNotify('Agent run not found in archive.', 'warning');
          return;
        }
        setSelectedAgentRun(detail);
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        showNotify(`Failed to load agent run: ${message}`, 'error');
      });
  };

  const stackBusy =
    Boolean(activeRunId && liveRun?.status !== 'completed') ||
    Boolean(uniqueBusy) ||
    shipBusy;

  const laneSummaries = laneSummariesData?.yazilimTalepLaneSummaries ?? [];

  return (
    <div className="ops-app">
      <header className="ops-topbar">
        <div className="ops-brand">
          <span className="ops-brand__mark" aria-hidden />
          <div>
            <h1 className="ops-brand__title">Lanes</h1>
            <p className="ops-brand__subtitle">Software delivery lanes</p>
          </div>
        </div>

        <Toolbar className="ops-topbar__toolbar">
          <ToolbarItem location="before">
            <Button
              icon="refresh"
              hint="Refresh lanes"
              stylingMode="text"
              onClick={() => void refetchLaneSummaries()}
            />
          </ToolbarItem>
          <ToolbarItem location="after" locateInMenu="never">
            <span className="ops-user">
              <i className="dx-icon dx-icon-user" aria-hidden />
              {username ?? 'admin'}
            </span>
          </ToolbarItem>
          <ToolbarItem location="after">
            <Button
              text="Sign out"
              icon="runner"
              stylingMode="text"
              onClick={() => void logout()}
            />
          </ToolbarItem>
        </Toolbar>
      </header>

      <main className="ops-main">
        <section className="ops-panel">
          <TabPanel
            className="ops-tabs"
            animationEnabled
            swipeEnabled={false}
            deferRendering={false}
          >
            <TabItem title="Unique Delphi" icon="product">
              <div className="ops-tab-content">
                <Toolbar className="ops-section-toolbar">
                  <ToolbarItem location="before">
                    <span className="ops-section-title">
                      Build & publish Unique Delphi packages (GitHub Actions)
                    </span>
                  </ToolbarItem>
                </Toolbar>

                <div className="unique-form">
                  <TextBox
                    label="SHA (optional)"
                    stylingMode="outlined"
                    value={uniqueSha}
                    onValueChanged={(e) =>
                      setUniqueSha(typeof e.value === 'string' ? e.value : '')
                    }
                    hint="Exact commit SHA. Empty = workflow default (HEAD of ref)."
                    width="100%"
                  />
                  <TextBox
                    label="Ref (build only, optional)"
                    stylingMode="outlined"
                    value={uniqueRef}
                    onValueChanged={(e) =>
                      setUniqueRef(typeof e.value === 'string' ? e.value : '')
                    }
                    hint="Branch/tag/SHA for build-delphi. Ignored when SHA is set."
                    width="100%"
                  />
                  <CheckBox
                    text="Dry run (publish only)"
                    value={uniqueDryRun}
                    onValueChanged={(e) => setUniqueDryRun(Boolean(e.value))}
                  />
                  <div className="stack-actions">
                    <Button
                      text="Build Unique"
                      icon="runner"
                      type="default"
                      stylingMode="contained"
                      disabled={stackBusy}
                      onClick={() => handleUniqueAction('build')}
                    />
                    <Button
                      text="Publish Unique"
                      icon="upload"
                      type="success"
                      stylingMode="contained"
                      disabled={stackBusy}
                      onClick={() => handleUniqueAction('publish')}
                    />
                  </div>
                  <p className="muted unique-form__hint">
                    Dispatches the app repository's{' '}
                    workflows <code className="ops-code">build-delphi.yml</code> /{' '}
                    <code className="ops-code">publish-delphi.yml</code>. Build
                    writes to the workstation; publish copies Release to the UNC
                    share. Prefer{' '}
                    <strong>Yazılım Talep → Güncelle</strong> when publishing a
                    talep (SHA locked to the ship session). This tab is ad-hoc
                    Delphi tooling only.
                  </p>
                </div>
              </div>
            </TabItem>

            <TabItem title="Yazılım Talep" icon="comment">
              <div className="ops-tab-content">
                <Toolbar className="ops-section-toolbar">
                  <ToolbarItem location="before">
                    <span className="ops-section-title">
                      Seven lanes · Planla → Uygula → Kontrol → Test → Kabul →
                      Güncelle · Geri Al
                    </span>
                  </ToolbarItem>
                </Toolbar>

                <div className="unique-form">
                  <Toolbar className="ops-section-toolbar">
                    <ToolbarItem location="before">
                      <span className="ops-section-title">
                        UNIQUE_DB.api · Canlı Flyway (no talep)
                      </span>
                    </ToolbarItem>
                  </Toolbar>
                  <p className="muted unique-form__hint">
                    Dispatches the database-API repository's{' '}
                    <code className="ops-code">migrate-unique-db-canli.yml</code>.
                    Validate is read-only. Migrate needs CANLI UYGULA. Stage
                    stays on the other workflow.
                  </p>
                  <TextBox
                    label="Type CANLI UYGULA to Güncelle api"
                    stylingMode="outlined"
                    value={canliConfirm}
                    onValueChanged={(e) =>
                      setCanliConfirm(
                        typeof e.value === 'string' ? e.value : '',
                      )
                    }
                    width="100%"
                  />
                  <div className="stack-actions">
                    <Button
                      text="Validate api"
                      stylingMode="outlined"
                      disabled={Boolean(uniqueDbApiBusy)}
                      onClick={() => handleUniqueDbApi('validate')}
                    />
                    <Button
                      text="Güncelle api"
                      type="danger"
                      stylingMode="contained"
                      disabled={Boolean(uniqueDbApiBusy)}
                      onClick={() => handleUniqueDbApi('migrate')}
                    />
                  </div>
                </div>

                <div className="unique-form">
                  <Toolbar className="ops-section-toolbar">
                    <ToolbarItem location="before">
                      <span className="ops-section-title">
                        Past taleps (touched)
                      </span>
                    </ToolbarItem>
                  </Toolbar>
                  <DataGrid
                    dataSource={laneSummaries}
                    keyExpr="talepId"
                    {...GRID_COMMON}
                    height={220}
                    onRowClick={(e) => {
                      const row = e.data as YazilimTalepLaneSummary | undefined;
                      if (row?.talepId) handleLoadLaneStatus(row.talepId);
                    }}
                  >
                    <SearchPanel visible placeholder="Search past taleps…" />
                    <Scrolling mode="virtual" />
                    <Column dataField="talepId" caption="TalepId" width={90} />
                    <Column
                      dataField="updatedAt"
                      caption="Updated"
                      width={160}
                    />
                    <Column
                      dataField="planla"
                      caption="Planla"
                      width={70}
                      calculateCellValue={(row: YazilimTalepLaneSummary) =>
                        laneBoolMark(row.planla)
                      }
                    />
                    <Column
                      dataField="uygula"
                      caption="Uygula"
                      width={70}
                      calculateCellValue={(row: YazilimTalepLaneSummary) =>
                        laneBoolMark(row.uygula)
                      }
                    />
                    <Column
                      caption="Kontrol"
                      width={110}
                      calculateCellValue={(row: YazilimTalepLaneSummary) => {
                        const mark = laneBoolMark(row.kontrol);
                        return row.kontrolVerdict
                          ? `${mark} ${row.kontrolVerdict}`
                          : mark;
                      }}
                    />
                    <Column
                      dataField="test"
                      caption="Test"
                      width={60}
                      calculateCellValue={(row: YazilimTalepLaneSummary) =>
                        laneBoolMark(row.test)
                      }
                    />
                    <Column
                      dataField="kabul"
                      caption="Kabul"
                      width={60}
                      calculateCellValue={(row: YazilimTalepLaneSummary) =>
                        laneBoolMark(row.kabul)
                      }
                    />
                    <Column
                      dataField="guncelle"
                      caption="Güncelle"
                      width={80}
                      calculateCellValue={(row: YazilimTalepLaneSummary) =>
                        laneBoolMark(row.guncelle)
                      }
                    />
                    <Column
                      dataField="geriAl"
                      caption="Geri Al"
                      width={70}
                      calculateCellValue={(row: YazilimTalepLaneSummary) =>
                        laneBoolMark(row.geriAl)
                      }
                    />
                    <Column
                      dataField="lastLane"
                      caption="Last lane"
                      width={100}
                    />
                  </DataGrid>

                  <NumberBox
                    label="TalepId"
                    stylingMode="outlined"
                    value={talepId ?? undefined}
                    onValueChanged={(e) =>
                      setTalepId(
                        typeof e.value === 'number' && Number.isFinite(e.value)
                          ? Math.trunc(e.value)
                          : null,
                      )
                    }
                    min={1}
                    showSpinButtons
                    width="100%"
                  />
                  <div className="stack-actions">
                    <Button
                      text="Load lane status"
                      icon="pulldown"
                      type="default"
                      stylingMode="contained"
                      disabled={stackBusy}
                      onClick={() => handleLoadLaneStatus()}
                    />
                    <Button
                      text="Load context (C0)"
                      icon="pulldown"
                      stylingMode="outlined"
                      disabled={stackBusy}
                      hint="C0 checklist + agent runs + lane status"
                      onClick={handleLoadShipContext}
                    />
                  </div>
                  {laneStatus ? (
                    <p className="muted unique-form__hint">
                      Kontrol:{' '}
                      <strong>
                        {laneStatus.kontrolVerdict ?? 'never'}
                      </strong>
                      {laneStatus.kontrolFresh ? ' · fresh' : ' · stale/missing'}
                      {' · '}
                      status{' '}
                      <strong>
                        {laneStatus.statusLabelTr ||
                          laneStatus.statusPhase ||
                          'idle'}
                      </strong>
                      {laneStatus.statusInFlight ? ' (in flight)' : ''}
                      {laneStatus.tipSet.unique
                        ? ` · tip ${laneStatus.tipSet.unique.slice(0, 8)}`
                        : ''}
                    </p>
                  ) : (
                    <p className="muted unique-form__hint">
                      Load lane status (or C0 context) to enable the seven
                      buttons from ops-api SoT.
                    </p>
                  )}
                  <div className="stack-actions">
                    {(
                      [
                        ['planla', 'Planla', laneStatus?.planla],
                        ['uygula', 'Uygula', laneStatus?.uygula],
                        ['kontrol', 'Kontrol Et', laneStatus?.kontrol],
                      ] as const
                    ).map(([lane, label, btn]) => (
                      <Button
                        key={lane}
                        text={label}
                        stylingMode="outlined"
                        disabled={
                          stackBusy ||
                          !btn?.visible ||
                          !btn.enabled
                        }
                        hint={
                          btn?.hardBlockers?.length
                            ? btn.hardBlockers.join(', ')
                            : btn?.warnings?.join(', ') || label
                        }
                        onClick={() => handleStartLane(lane)}
                      />
                    ))}
                  </div>
                  <div className="stack-actions">
                    {(
                      [
                        ['test', 'Test Et', laneStatus?.test],
                        ['kabul', 'Kabul Et', laneStatus?.kabul],
                        ['guncelle', 'Güncelle', laneStatus?.guncelle],
                        ['geriAl', 'Geri Al', laneStatus?.geriAl],
                      ] as const
                    ).map(([lane, label, btn]) => (
                      <Button
                        key={lane}
                        text={label}
                        type={
                          lane === 'guncelle' || lane === 'geriAl'
                            ? 'danger'
                            : 'normal'
                        }
                        stylingMode={
                          lane === 'guncelle' || lane === 'geriAl'
                            ? 'contained'
                            : 'outlined'
                        }
                        disabled={
                          stackBusy ||
                          !btn?.visible ||
                          !btn.enabled
                        }
                        hint={
                          btn?.hardBlockers?.length
                            ? btn.hardBlockers.join(', ')
                            : btn?.warnings?.join(', ') || label
                        }
                        onClick={() => handleStartLane(lane)}
                      />
                    ))}
                  </div>
                  <p className="muted unique-form__hint">
                    Güncelle: Unique.DB → Canlı → UNIQUE_DB.api Flyway → merge
                    (Unique + portal) → build/publish → Boarder → Amplify/SAM.
                    Resume skips Canlı when already verified.
                  </p>
                  {laneStatus?.guncelle.requiresKontrolAtla ? (
                    <>
                      <TextBox
                        label="Type KONTROL ATLA to ship without fresh Kontrol"
                        stylingMode="outlined"
                        value={kontrolAtla}
                        onValueChanged={(e) =>
                          setKontrolAtla(
                            typeof e.value === 'string' ? e.value : '',
                          )
                        }
                        width="100%"
                      />
                      <TextBox
                        label="kontrolAtlaReason (required)"
                        stylingMode="outlined"
                        value={kontrolAtlaReason}
                        onValueChanged={(e) =>
                          setKontrolAtlaReason(
                            typeof e.value === 'string' ? e.value : '',
                          )
                        }
                        width="100%"
                      />
                    </>
                  ) : null}
                  {laneStatus?.guncelle.requiresCanliConfirm ? (
                    <TextBox
                      label="Type CANLI UYGULA for Güncelle live SQL"
                      stylingMode="outlined"
                      value={canliConfirm}
                      onValueChanged={(e) =>
                        setCanliConfirm(
                          typeof e.value === 'string' ? e.value : '',
                        )
                      }
                      width="100%"
                    />
                  ) : null}
                  {laneStatus ? (
                    <TextBox
                      label="Type CANLI GERI AL for Geri Al"
                      stylingMode="outlined"
                      value={geriAlConfirm}
                      onValueChanged={(e) =>
                        setGeriAlConfirm(
                          typeof e.value === 'string' ? e.value : '',
                        )
                      }
                      width="100%"
                    />
                  ) : null}
                  {laneResult ? (
                    <TextArea
                      label="Last lane result"
                      stylingMode="outlined"
                      value={[
                        `${laneResult.lane} · ok=${String(laneResult.ok)} · ${laneResult.message}`,
                        laneResult.detail ?? '',
                        laneResult.url ?? '',
                      ]
                        .filter(Boolean)
                        .join('\n')}
                      readOnly
                      autoResizeEnabled
                      minHeight={80}
                      width="100%"
                    />
                  ) : null}

                  <Toolbar className="ops-section-toolbar">
                    <ToolbarItem location="before">
                      <span className="ops-section-title">
                        Observe · Agent runs · Boards · C0 checklist
                      </span>
                    </ToolbarItem>
                  </Toolbar>
                  {shipContext ? (
                    <div className="muted" style={{ marginBottom: 8 }}>
                      Verdict: <strong>{shipContext.verdict}</strong>
                      {shipContext.statusLabelTr
                        ? ` · ${shipContext.statusLabelTr}`
                        : ''}
                      {shipContext.tipSha
                        ? ` · tip ${shipContext.tipSha.slice(0, 12)}…`
                        : ' · no tip SHA'}
                      {shipContext.prUrl ? (
                        <>
                          {' '}
                          ·{' '}
                          <a
                            href={shipContext.prUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            PR #{shipContext.prNumber}
                          </a>
                        </>
                      ) : (
                        ' · no open PR'
                      )}
                      {' · '}
                      scripts: {shipContext.dbScripts.length}
                      {shipContext.visibility.delphiOnly
                        ? ' · Delphi-only'
                        : ''}
                    </div>
                  ) : (
                    <p className="muted unique-form__hint">
                      Load context to unlock Observe grids and C0 checklist.
                    </p>
                  )}
                  <DataGrid
                    dataSource={agentRuns}
                    keyExpr="runId"
                    {...GRID_COMMON}
                    height={220}
                    onRowClick={(e) => {
                      const row = e.data as YazilimTalepAgentRunSummary | undefined;
                      if (row?.runId) handleOpenAgentRun(row.runId);
                    }}
                  >
                    <SearchPanel visible placeholder="Search runs…" />
                    <Scrolling mode="virtual" />
                    <Column dataField="startedAt" caption="Started" width={160} />
                    <Column dataField="status" caption="Status" width={100} />
                    <Column
                      dataField="outcomeKind"
                      caption="Outcome"
                      width={110}
                    />
                    <Column
                      dataField="summaryForUser"
                      caption="Summary"
                      width={280}
                    />
                    <Column dataField="runId" caption="Run id" width={240} />
                  </DataGrid>
                  {selectedAgentRun ? (
                    <TextArea
                      label={`Archive · ${selectedAgentRun.runId} (${selectedAgentRun.status})`}
                      stylingMode="outlined"
                      value={[
                        selectedAgentRun.summaryForUser ?? '',
                        '',
                        '--- agent result ---',
                        selectedAgentRun.agentResult || '(empty)',
                        '',
                        '--- prompt (head) ---',
                        (selectedAgentRun.prompt || '').slice(0, 8000),
                      ].join('\n')}
                      readOnly
                      autoResizeEnabled
                      minHeight={200}
                      width="100%"
                    />
                  ) : null}

                  <div className="stack-actions" style={{ marginTop: 12 }}>
                    <Button
                      text="Probe boards"
                      icon="chart"
                      stylingMode="outlined"
                      disabled={stackBusy}
                      onClick={handleProbeBoards}
                    />
                  </div>
                  {boardsError ? (
                    <p className="status-down">{boardsError}</p>
                  ) : null}
                  <DataGrid
                    dataSource={boardsRows}
                    keyExpr="name"
                    {...GRID_COMMON}
                    height={200}
                  >
                    <Column dataField="name" caption="Channel" width={160} />
                    <Column dataField="status" caption="Status" width={100} />
                    <Column dataField="lastExec" caption="LastExec" width={170} />
                    <Column
                      dataField="ageSeconds"
                      caption="Age (s)"
                      width={80}
                    />
                    <Column
                      dataField="runCount"
                      caption="Runs"
                      width={70}
                    />
                    <Column
                      dataField="sqlTextHead"
                      caption="SQL head"
                      width={260}
                    />
                  </DataGrid>
                  {shipContext ? (
                    <TextArea
                      label="C0 checklist (raw JSON)"
                      stylingMode="outlined"
                      value={shipContext.rawJson}
                      readOnly
                      autoResizeEnabled
                      minHeight={140}
                      width="100%"
                    />
                  ) : null}
                </div>
              </div>
            </TabItem>
          </TabPanel>
        </section>
      </main>

      <Popup
        visible={Boolean(liveRun)}
        onHiding={() => {
          setActiveRunId(null);
          setLiveRun(null);
        }}
        title={
          liveRun
            ? workflowTitle(liveRun.action, activeStackName)
            : 'Workflow run'
        }
        showCloseButton
        dragEnabled
        hideOnOutsideClick
        width="90vw"
        maxWidth={960}
        height="85vh"
        maxHeight={720}
      >
        <div className="ops-popup-body">
          {liveRun ? (
            <>
              <div className="ops-popup-meta">
                <StatusBadge
                  status={
                    liveRun.status === 'completed'
                      ? liveRun.conclusion === 'success'
                        ? 'HEALTHY'
                        : 'DOWN'
                      : 'DEGRADED'
                  }
                />
                <span className="muted">
                  {statusLabel(liveRun.status, liveRun.conclusion)}
                  {liveRun.id !== 'unknown' ? (
                    <>
                      {' '}
                      · run <code className="ops-code">{liveRun.id}</code>
                    </>
                  ) : null}
                </span>
                {liveRun.url ? (
                  <a
                    className="ops-link"
                    href={liveRun.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open in GitHub
                  </a>
                ) : null}
              </div>

              {workflowSteps.length > 0 ? (
                <DataGrid
                  {...GRID_COMMON}
                  dataSource={workflowSteps}
                  keyExpr="key"
                  className="ops-grid ops-grid--nested run-steps-grid"
                  height={220}
                >
                  <Column dataField="name" caption="Step" />
                  <Column
                    caption="Status"
                    width={140}
                    calculateCellValue={(row: WorkflowStep) =>
                      statusLabel(row.status, row.conclusion)
                    }
                  />
                </DataGrid>
              ) : null}

              <LogViewer
                text={
                  liveRun.logs?.trim()
                    ? liveRun.logs
                    : liveRun.status === 'completed'
                      ? 'No logs available.'
                      : 'Waiting for logs…'
                }
              />
            </>
          ) : null}
        </div>
      </Popup>
    </div>
  );
};
